import { computed, ref } from 'vue'
import { freezeObject } from '@/global/utils/utils'
import TabActionButtons from '@/global/components/view-layouts/tabs-layout/components/TabActionButtons.vue'
import BaseDetailsInfo from '@/modules/road-maintenance-module/winter-service-v2/bases/components/BaseDetailsInfo.vue'
import { useBaseFields } from '@/modules/road-maintenance-module/winter-service-v2/bases/composables/useBaseFields'
import {
  applyBaseForm,
  clearedBasePhoto,
  deleteBasePhoto,
  pickedBasePhoto,
  storedBasePhotoUrl,
  toBasePayload,
  uploadBasePhoto,
  withBaseForm
} from '@/modules/road-maintenance-module/winter-service-v2/bases/composables/baseData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  BASES_ENDPOINT,
  BASES_INDEX_ROUTE,
  BASES_TRANSLATION_NAMESPACE as NS
} from '@/modules/road-maintenance-module/winter-service-v2/bases/config/constants'

const TAB_KEY = 'base_details'

export function useBaseDetails (options) {
  const {
    translate,
    resolveRouteParamValue,
    openGeolocationDialogFn
  } = options || {}

  const { fields } = useBaseFields({
    translate,
    openGeolocationDialogFn
  })

  // The photo has endpoints of its own, so it is settled around the save rather
  // than inside it: what the user did to the panel is read on the way out - that
  // is the last point the picked File is still around, Content.vue having replaced
  // the form data with the saved record by the time `afterSave` runs - and acted on
  // once the record is there. Clearing the panel only counts as a deletion against
  // the photo the record arrived with, which is what `storedPhotoUrl` keeps.
  const storedPhotoUrl = ref(null)
  const pendingPhoto = ref(null)
  const isPhotoCleared = ref(false)

  const apiConfig = freezeObject({
    get: {
      module: WINTER_SERVICE_MODULE,
      route: `${BASES_ENDPOINT}/${resolveRouteParamValue('id')}`
    },
    // Sent as POST with `_method=PATCH`, which is what the update route expects.
    post: {
      module: WINTER_SERVICE_MODULE,
      route: `${BASES_ENDPOINT}/${resolveRouteParamValue('id')}`
    }
  })

  const tabActions = computed(() => ({
    component: TabActionButtons,
    title: translate(`${NS}.base_details`),
    showEditButtons: true,
    showBackButton: {
      uiViewName: BASES_INDEX_ROUTE
    },
    visible: true
  }))

  // `rows` is not just how the component finds its fields - Content.vue's
  // applyFormValuesForSend walks `tabContent[].rows[].fields[]` to build the save
  // payload, so a block that keeps its fields anywhere else saves nothing.
  const infoBlock = computed(() => ({
    component: BaseDetailsInfo,
    visible: true,
    rows: [
      {
        type: 'fields',
        columnsCount: 1,
        fields: fields.value
      }
    ]
  }))

  // The backend reports a base nested (address on `geolocation`, contact on
  // `responsible_person`, the photo on `photo`, an `is_active` flag), the form edits
  // it flat - both the fetched and the just saved record have to be translated
  // before the fields see it.
  const onDataReceived = (apiData) => {
    storedPhotoUrl.value = storedBasePhotoUrl(apiData?.data)
    applyBaseForm(apiData?.data)
  }

  const modifyDataBeforeSend = ({ copiedData, jsonData }) => {
    pendingPhoto.value = pickedBasePhoto(jsonData)
    isPhotoCleared.value = clearedBasePhoto(jsonData, storedPhotoUrl.value)

    return toBasePayload(copiedData)
  }

  const settlePhoto = async (data) => {
    if (pendingPhoto.value) {
      return await uploadBasePhoto(data?.id, pendingPhoto.value) || data
    }

    if (isPhotoCleared.value) {
      await deleteBasePhoto(data?.id)

      // The delete endpoint answers with a message, not the base - the record just
      // saved is still current apart from the photo that is now gone.
      return {
        ...data,
        photo: null
      }
    }

    return data
  }

  const afterSave = async (context, data) => {
    let saved = data

    try {
      saved = await settlePhoto(data)
    }
    catch (exception) {
      // The base itself is saved and the api layer has already reported the failed
      // photo call - all that is left is to show the record as it stands.
      console.error(exception)
    }
    finally {
      pendingPhoto.value = null
      isPhotoCleared.value = false
    }

    storedPhotoUrl.value = storedBasePhotoUrl(saved)
    context?.refreshData?.(withBaseForm(saved))
  }

  return computed(() => ({
    key: TAB_KEY,
    apiConfig,
    onDataReceived,
    afterSave,
    modifyDataBeforeSend,
    // Coordinates are set through the address picker, never typed - they have no
    // field of their own, so they have to be declared to survive a save.
    hiddenFormKeys: [
      'latitude',
      'longitude'
    ],
    tabContent: [
      tabActions.value,
      infoBlock.value
    ]
  }))
}
